import { useMemo, useState } from 'react';
import { AvisoTope } from '../componentes/AvisoTope';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { TIPO_VEHICULO, Etiqueta, tonoDe } from '../componentes/Etiqueta';
import { dinero, haceCuanto, laFechaHora } from '../componentes/formato';
import { useCargaReal } from '../dominio/carga';
import { TOPE_FILAS_VIAJES, obtenerViajes } from '../dominio/viajesApi';
import type { TarifaReferencialListado } from '../dominio/viajesApi';
import { ESTADOS_VIAJE_TODOS } from '../dominio/contratos';
import type { CanceladoPor, EstadoViaje } from '../dominio/contratos';

/**
 * 🔑 Acá SÍ se reusan `Etiqueta`/`TIPO_VEHICULO` de `componentes/Etiqueta.tsx`, al revés que en
 * `Conductores.tsx`. No es incoherencia: es que los valores se cotejaron uno a uno.
 * `EstadoViaje` y `TipoVehiculo` de `@taxi/contratos` coinciden exactamente con los enums de
 * `apps/api/prisma/schema.prisma` (9 y 2 valores respectivamente); los de conductores no.
 * Ver la cabecera de `dominio/viajesApi.ts`.
 */
const CANCELADO_POR_TEXTO: Record<CanceladoPor, string> = {
  PASAJERO: 'lo canceló el pasajero',
  CONDUCTOR: 'lo canceló el conductor',
  SISTEMA: 'lo canceló el sistema',
  OPERADOR: 'lo canceló un operador',
};

/**
 * La tarifa referencial, como rango.
 *
 * ⚠️ **`min`/`max` llegan como STRING** (`Prisma.Decimal` sobre HTTP): `Number()` es obligatorio
 * antes de formatear, y no es cosmético — `dinero()` recibe un `number` y un string ahí produciría
 * `NaN` silencioso en pantalla. Tolera además el caso `number`, que es lo que emite el doble de
 * pruebas del backend.
 *
 * ⚠️ `moneda` es `Char(3)` en el schema, o sea que el tipo no garantiza un código ISO-4217 válido
 * — y `Intl.NumberFormat` LANZA ante uno inválido, lo que tiraría la tabla entera por una fila
 * mal cargada. El `catch` degrada a texto plano en vez de romper la pantalla.
 */
function tarifaTexto(tarifa: TarifaReferencialListado): string {
  const min = Number(tarifa.min);
  const max = Number(tarifa.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return '—';
  try {
    return `${dinero(min, tarifa.moneda)} – ${dinero(max, tarifa.moneda)}`;
  } catch {
    return `${tarifa.moneda} ${min.toFixed(2)} – ${max.toFixed(2)}`;
  }
}

export function Viajes() {
  const [estado, setEstado] = useState<EstadoViaje | 'todos'>('todos');
  // `estado` va en las deps porque cambia la URL que se pide (es un parámetro de la consulta), y
  // se manda al backend en vez de filtrar acá: con `take: 200` ordenado por `solicitadoAt desc`,
  // filtrar client-side buscaría dentro de los 200 viajes más recientes y un estado poco
  // frecuente daría vacío teniendo filas de sobra en la base.
  const [carga, recargar] = useCargaReal(
    () => obtenerViajes(estado === 'todos' ? {} : { estado }),
    [estado],
  );

  const [texto, setTexto] = useState('');
  const ahora = new Date();

  const listos = carga.estado === 'listo' ? carga.datos : [];
  const enElTope = listos.length >= TOPE_FILAS_VIAJES;

  const resultados = useMemo(() => {
    const buscado = texto.trim().toLowerCase();
    if (buscado === '') return listos;
    // ⚠️ Los dos nombres son NULLABLE en el backend real (el mock tenía `pasajeroNombre` como
    // `string` siempre): sin las guardas, un viaje de un pasajero despersonalizado rompe la
    // búsqueda completa, no sólo su fila.
    return listos.filter(
      (viaje) =>
        viaje.id.toLowerCase().includes(buscado) ||
        (viaje.pasajeroNombre?.toLowerCase().includes(buscado) ?? false) ||
        (viaje.conductorNombre?.toLowerCase().includes(buscado) ?? false),
    );
  }, [listos, texto]);

  return (
    <Pagina
      rotulo="Viajes"
      titulo="Buscar viajes"
      ayuda="Datos reales del backend. Se busca por código, pasajero o conductor; el filtro de estado se resuelve en el servidor."
    >
      <div className="bloque">
        <div className="barra-filtros">
          <label className="campo">
            <span className="campo__etiqueta rotulo">Buscar</span>
            <input
              className="entrada"
              type="search"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              placeholder="Código, pasajero, conductor…"
            />
          </label>
          <label className="campo">
            <span className="campo__etiqueta rotulo">Estado</span>
            <select
              className="selector"
              value={estado}
              onChange={(evento) => setEstado(evento.target.value as EstadoViaje | 'todos')}
            >
              <option value="todos">Todos</option>
              {ESTADOS_VIAJE_TODOS.map((valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              ))}
            </select>
          </label>
          <span className="tenue">
            {resultados.length} de {listos.length}
            {enElTope ? ' (tope)' : ''}
          </span>
        </div>

        {carga.estado === 'listo' && enElTope ? (
          <AvisoTope
            tope={TOPE_FILAS_VIAJES}
            sugerencia="Acotá con «Estado»: ese filtro sí viaja al servidor y hace que el tope se aplique al conjunto ya filtrado."
          />
        ) : null}

        {carga.estado === 'cargando' ? (
          <Cargando filas={5} etiqueta="Cargando viajes" />
        ) : carga.estado === 'error' ? (
          <ConError detalle={carga.mensaje} alReintentar={recargar} />
        ) : resultados.length === 0 ? (
          <Vacio titulo="Ningún viaje coincide" detalle="Probá sin el filtro de estado." />
        ) : (
          <Tabla
            cabeceras={[
              'Código',
              'Estado',
              'Pasajero',
              'Conductor',
              'Clase',
              { texto: 'Tarifa referencial', num: true },
              'Solicitado',
              'Cerró',
            ]}
          >
            {resultados.map((viaje) => (
              <tr key={viaje.id} data-estado={tonoDe('viaje', viaje.estado)}>
                <td style={{ fontFamily: 'var(--mono, monospace)' }}>{viaje.id}</td>
                <td>
                  <Etiqueta tipo="viaje" valor={viaje.estado} />
                  {viaje.canceladoPor ? (
                    <div className="apagado" style={{ fontFamily: 'var(--sans)' }}>
                      {CANCELADO_POR_TEXTO[viaje.canceladoPor]}
                    </div>
                  ) : null}
                  {viaje.motivoCancelacion ? (
                    <div className="apagado" style={{ fontFamily: 'var(--sans)', maxWidth: 210 }}>
                      {viaje.motivoCancelacion}
                    </div>
                  ) : null}
                </td>
                <td>
                  {viaje.pasajeroNombre ?? <span className="apagado">sin nombre</span>}
                  <div className="apagado">{viaje.pasajeroId}</div>
                </td>
                <td>
                  {viaje.conductorId ? (
                    <>
                      {viaje.conductorNombre ?? <span className="apagado">sin nombre</span>}
                      <div className="apagado">{viaje.conductorId}</div>
                    </>
                  ) : (
                    <span className="apagado">—</span>
                  )}
                </td>
                <td>{TIPO_VEHICULO[viaje.claseSolicitada]}</td>
                <td className="tabla__num">
                  {viaje.tarifaReferencial ? (
                    tarifaTexto(viaje.tarifaReferencial)
                  ) : (
                    <span className="apagado">sin resolver</span>
                  )}
                </td>
                <td className="tenue">
                  {haceCuanto(viaje.solicitadoAt, ahora)}
                  <div className="apagado">{laFechaHora(viaje.solicitadoAt)}</div>
                </td>
                {/*
                  Un solo huequito para el cierre: `finAt` y `canceladoAt` son excluyentes por la
                  máquina de estados, así que dos columnas estarían vacías la mitad del tiempo cada
                  una. `asignadoAt` no tiene columna propia a propósito: no aporta a la lectura
                  operativa de la tabla y ya viaja en el dato para quien lo necesite.
                */}
                <td className="tenue">
                  {viaje.finAt
                    ? laFechaHora(viaje.finAt)
                    : viaje.canceladoAt
                      ? laFechaHora(viaje.canceladoAt)
                      : '—'}
                </td>
              </tr>
            ))}
          </Tabla>
        )}
      </div>

      {/*
        🔴 El panel «Ofertas de <viaje>» se retiró al conectar esta pantalla al backend, y conviene
        que quede escrito POR QUÉ: no fue simplificación.
      */}
      <Bloque titulo="Por qué ya no están las ofertas de cada viaje">
        <p className="pagina__ayuda" style={{ marginTop: 0 }}>
          Las ofertas de un viaje son lo que explica por qué terminó como terminó — <em>perdida</em>{' '}
          (aceptó y otro ganó) y <em>expirada</em> (no contestó) son cosas distintas, y colapsarlas
          haría imposible calcular la tasa de victoria del panel. Pero{' '}
          <strong>
            el backend no expone hoy ningún endpoint que liste las ofertas de un viaje
          </strong>
          : el módulo de dispatch tiene aceptar, buscar, vencimientos y tasa de victoria, y ninguno
          devuelve esa tabla. Dejar el panel con datos de ejemplo al lado de filas reales sería la
          peor opción de las tres: quien lo lea no tiene forma de saber que esa mitad de la pantalla
          no ocurrió.
        </p>
        <p className="pagina__ayuda">
          Tampoco están las columnas de <strong>origen y destino</strong>, ni la{' '}
          <strong>recogida estimada</strong> ni la <strong>distancia</strong>: el listado admin no
          los trae (es «lo suficiente para operar, no el detalle completo») y no hay un endpoint de
          detalle de viaje donde buscarlos. Cinco columnas con un guión permanente se leen como
          «este viaje no tuvo ruta», que es falso.
        </p>
      </Bloque>
    </Pagina>
  );
}
